import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, MapPin, Globe, Briefcase, User, Loader2, Plus, Trash2, Navigation, AlertTriangle, Check, X, Users, PawPrint, Pencil, ExternalLink, Coffee, Bug } from 'lucide-react';
import { geocodeCityCountry, reverseGeocode } from '../utils/geocode';
import LocationPreview from '../components/LocationPreview';
import type { EmployeeRow } from '../lib/database.types';
import { useAuth } from '../contexts/AuthContext';
import { useEmployee } from '../contexts/EmployeeContext';
import { supabase } from '../lib/supabase';
import type { EmployeeUpdate } from '../lib/database.types';
import type { TemporaryLocation, Team, Pet } from '../types/employee';
import { fetchAllTeams, addEmployeeTeam, removeEmployeeTeam, createTeam, fetchPetsForEmployee, createPet, updatePet, deletePet } from '../utils/api';
import { KNOWN_SPECIES, petSpeciesIcon } from '../utils/petUtils';
import AvatarUpload from '../components/AvatarUpload';
import TeamIcon from '../components/TeamIcon';
import { captureMutationError } from '../utils/errorReporting';
import TeamIconPicker from '../components/TeamIconPicker';

interface LocationState {
  claimedProfile?: EmployeeRow;
}

interface GeocodeSuggestion {
  lat: string;
  lon: string;
  timezone: string | null;
}

interface FormState {
  name: string;
  role: string;
  department: string;
  employer: string;
  email: string;
  phone: string;
  bio: string;
  start_date: string;
  skills: string;
  languages: string;
  city: string;
  country: string;
  latitude: string;
  longitude: string;
  timezone: string;
  avatar_url: string;
  avatar_hover_url: string;
  social_linkedin: string;
  social_twitter: string;
  social_github: string;
  social_slack: string;
  new_hire_deck_url: string;
  coordinates_manual: boolean;
  meet_colleague_available: boolean;
  meet_colleague_unavailable_until: string;
}

const EditProfilePage = ({ isOnboarding = false }: { isOnboarding?: boolean }) => {
  const { user, employeeProfile, refreshProfile } = useAuth();
  const { reloadEmployees } = useEmployee();
  const navigate = useNavigate();
  const location = useLocation();
  const { claimedProfile } = (location.state as LocationState) ?? {};

  const [form, setForm] = useState<FormState>({
    name: '',
    role: '',
    department: '',
    employer: '',
    email: '',
    phone: '',
    bio: '',
    start_date: '',
    skills: '',
    languages: '',
    city: '',
    country: '',
    latitude: '',
    longitude: '',
    timezone: '',
    avatar_url: '',
    avatar_hover_url: '',
    social_linkedin: '',
    social_twitter: '',
    social_github: '',
    social_slack: '',
    new_hire_deck_url: '',
    coordinates_manual: false,
    meet_colleague_available: true,
    meet_colleague_unavailable_until: '',
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coordError, setCoordError] = useState<string | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  // Pending geocode suggestion when coordinates_manual=true — shown as a confirmation prompt
  const [geocodeSuggestion, setGeocodeSuggestion] = useState<GeocodeSuggestion | null>(null);
  const geocodeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Teams state
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [myTeamIds, setMyTeamIds] = useState<Set<string>>(new Set());
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamIcon, setNewTeamIcon] = useState<{ iconType: Team['iconType']; iconValue: string }>({ iconType: 'emoji', iconValue: '' });
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);

  // Pets state
  const [pets, setPets] = useState<Pet[]>([]);
  const [petsLoading, setPetsLoading] = useState(false);
  const [showPetForm, setShowPetForm] = useState(false);
  const [editingPetId, setEditingPetId] = useState<string | null>(null);
  const [petForm, setPetForm] = useState({ name: '', species: 'cat', role: '', bio: '', photo_url: '' });
  const [petStorageKey, setPetStorageKey] = useState('');
  const [petError, setPetError] = useState<string | null>(null);
  const [petSaving, setPetSaving] = useState(false);

  // Temporary locations state
  const [tempLocs, setTempLocs] = useState<TemporaryLocation[]>([]);
  const [tempLocsLoading, setTempLocsLoading] = useState(false);
  const [showTempLocForm, setShowTempLocForm] = useState(false);
  const [tempLocForm, setTempLocForm] = useState({ city: '', country: '', timezone: '', label: '', start_date: '', end_date: '', latitude: '', longitude: '' });
  const [tempLocGeocoding, setTempLocGeocoding] = useState(false);
  const [tempLocError, setTempLocError] = useState<string | null>(null);
  const tempLocGeoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Use the live profile from context, or fall back to the claimed profile passed via
    // router state (handles the race where context hasn't refreshed by the time we navigate)
    const source = employeeProfile ?? claimedProfile ?? null;
    if (source) {
      const sm = source.social_media ?? {};
      setForm({
        name: source.name,
        role: source.role,
        department: source.department,
        employer: source.employer,
        email: source.email,
        phone: source.phone ?? '',
        bio: source.bio,
        start_date: source.start_date ?? '',
        skills: (source.skills ?? []).join(', '),
        languages: (source.languages ?? []).join(', '),
        city: source.city,
        country: source.country,
        latitude: String(source.latitude ?? ''),
        longitude: String(source.longitude ?? ''),
        timezone: source.timezone,
        avatar_url: source.avatar_url ?? '',
        avatar_hover_url: source.avatar_hover_url ?? '',
        social_linkedin: sm.linkedin ?? '',
        social_twitter: sm.twitter ?? '',
        social_github: sm.github ?? '',
        social_slack: sm.slack ?? '',
        new_hire_deck_url: source.new_hire_deck_url ?? '',
        coordinates_manual: source.coordinates_manual ?? false,
        meet_colleague_available: source.meet_colleague_available ?? true,
        meet_colleague_unavailable_until: source.meet_colleague_unavailable_until ?? '',
      });
    } else if (user) {
      // Pre-fill email from auth user
      setForm(f => ({ ...f, email: user.email ?? '', name: user.user_metadata?.full_name ?? '' }));
    }
  }, [employeeProfile, claimedProfile, user]);

  // Load all teams and this employee's memberships
  useEffect(() => {
    const profileId = employeeProfile?.id ?? claimedProfile?.id ?? null;
    setTeamsLoading(true);
    Promise.all([
      fetchAllTeams(),
      profileId
        ? supabase.from('employee_teams').select('team_id').eq('employee_id', profileId)
        : Promise.resolve({ data: [] }),
    ]).then(([teams, memberships]) => {
      setAllTeams(teams);
      setMyTeamIds(new Set((memberships as { data: { team_id: string }[] | null }).data?.map(r => r.team_id) ?? []));
      setTeamsLoading(false);
    }).catch(() => setTeamsLoading(false));
  }, [employeeProfile?.id, claimedProfile?.id]);

  const handleJoinTeam = async (team: Team) => {
    const profileId = employeeProfile?.id ?? claimedProfile?.id ?? null;
    if (!profileId || myTeamIds.has(team.id)) return;
    setMyTeamIds(prev => new Set([...prev, team.id]));
    setShowTeamDropdown(false);
    setTeamSearch('');
    try {
      await addEmployeeTeam(profileId, team.id);
    } catch {
      setMyTeamIds(prev => { const s = new Set(prev); s.delete(team.id); return s; });
    }
  };

  const handleLeaveTeam = async (teamId: string) => {
    const profileId = employeeProfile?.id ?? claimedProfile?.id ?? null;
    if (!profileId) return;
    setMyTeamIds(prev => { const s = new Set(prev); s.delete(teamId); return s; });
    try {
      await removeEmployeeTeam(profileId, teamId);
    } catch {
      setMyTeamIds(prev => new Set([...prev, teamId]));
    }
  };

  const handleCreateAndJoinTeam = async () => {
    const profileId = employeeProfile?.id ?? claimedProfile?.id ?? null;
    if (!newTeamName.trim()) { setTeamError('Team name is required.'); return; }
    if (!newTeamIcon.iconValue) { setTeamError('Please select an icon for the team.'); return; }
    setTeamError(null);
    setTeamSaving(true);
    try {
      const team = await createTeam(newTeamName, newTeamIcon.iconType, newTeamIcon.iconValue);
      setAllTeams(prev => [...prev, team].sort((a, b) => a.name.localeCompare(b.name)));
      if (profileId) {
        await addEmployeeTeam(profileId, team.id);
        setMyTeamIds(prev => new Set([...prev, team.id]));
      }
      setShowCreateTeam(false);
      setNewTeamName('');
      setNewTeamIcon({ iconType: 'emoji', iconValue: '' });
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : 'Failed to create team.');
    } finally {
      setTeamSaving(false);
    }
  };

  // Load pets for this employee
  useEffect(() => {
    const profileId = employeeProfile?.id ?? claimedProfile?.id ?? null;
    if (!profileId) return;
    setPetsLoading(true);
    fetchPetsForEmployee(profileId).then(data => {
      setPets(data);
      setPetsLoading(false);
    }).catch(() => setPetsLoading(false));
  }, [employeeProfile?.id, claimedProfile?.id]);

  const openNewPetForm = () => {
    setEditingPetId(null);
    setPetForm({ name: '', species: 'cat', role: '', bio: '', photo_url: '' });
    setPetStorageKey(`pet-new-${crypto.randomUUID()}`);
    setPetError(null);
    setShowPetForm(true);
  };

  const openEditPetForm = (pet: Pet) => {
    setEditingPetId(pet.id);
    setPetForm({ name: pet.name, species: pet.species, role: pet.role ?? '', bio: pet.bio, photo_url: pet.photoUrl ?? '' });
    setPetStorageKey(`pet-${pet.id}`);
    setPetError(null);
    setShowPetForm(true);
  };

  const handleSavePet = async () => {
    
    const profileId = employeeProfile?.id ?? claimedProfile?.id ?? null;
    if (!profileId) return;
    if (!petForm.name.trim()) { setPetError('Pet name is required.'); return; }
    if (!petForm.species.trim()) { setPetError('Species is required.'); return; }
    setPetError(null);
    setPetSaving(true);
    try {
      const payload = {
        name: petForm.name.trim(),
        species: petForm.species.trim(),
        role: petForm.role.trim() || null,
        bio: petForm.bio.trim(),
        photo_url: petForm.photo_url.trim() || null,
      };
      if (editingPetId) {
        const updated = await updatePet(editingPetId, payload);
        setPets(prev => prev.map(p => p.id === editingPetId ? updated : p));
      } else {
        const created = await createPet({ ...payload, employee_id: profileId });
        setPets(prev => [...prev, created]);
      }
      setShowPetForm(false);
      setEditingPetId(null);
    } catch (err) {
      setPetError(err instanceof Error ? err.message : 'Failed to save pet.');
    } finally {
      setPetSaving(false);
    }
  };

  const handleDeletePet = async (id: string) => {
    await deletePet(id);
    setPets(prev => prev.filter(p => p.id !== id));
  };

  // Load existing temporary locations for this employee
  useEffect(() => {
    const profileId = employeeProfile?.id ?? claimedProfile?.id ?? null;
    if (!profileId) return;
    setTempLocsLoading(true);
    supabase
      .from('temporary_locations')
      .select('*')
      .eq('employee_id', profileId)
      .order('start_date')
      .then(({ data }) => {
        setTempLocs(
          (data ?? []).map(r => ({
            id: r.id,
            employeeId: r.employee_id,
            city: r.city,
            country: r.country,
            coordinates: { latitude: r.latitude ?? 0, longitude: r.longitude ?? 0 },
            timezone: r.timezone,
            label: r.label,
            startDate: r.start_date,
            endDate: r.end_date,
          }))
        );
        setTempLocsLoading(false);
      });
  }, [employeeProfile?.id, claimedProfile?.id]);

  const tempLocFormRef = useRef(tempLocForm);
  useEffect(() => { tempLocFormRef.current = tempLocForm; }, [tempLocForm]);

  const handleTempLocFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTempLocForm(f => ({ ...f, [name]: value }));

    if (name === 'city' || name === 'country') {
      if (tempLocGeoTimer.current) clearTimeout(tempLocGeoTimer.current);
      tempLocGeoTimer.current = setTimeout(async () => {
        const latest = tempLocFormRef.current;
        const city = name === 'city' ? value : latest.city;
        const country = name === 'country' ? value : latest.country;
        if (!city || !country) return;
        setTempLocGeocoding(true);
        const result = await geocodeCityCountry(city, country);
        if (result) {
          setTempLocForm(prev => ({
            ...prev,
            latitude: result.lat,
            longitude: result.lon,
            ...(result.timezone ? { timezone: result.timezone } : {}),
          }));
        }
        setTempLocGeocoding(false);
      }, 600);
    }
  };

  const handleAddTempLoc = async () => {
    const profileId = employeeProfile?.id ?? claimedProfile?.id ?? null;
    if (!profileId) return;
    if (!tempLocForm.city || !tempLocForm.country || !tempLocForm.start_date || !tempLocForm.end_date) {
      setTempLocError('City, country, start date and end date are required.');
      return;
    }
    setTempLocError(null);

    const { data, error } = await supabase
      .from('temporary_locations')
      .insert({
        employee_id: profileId,
        city: tempLocForm.city.trim(),
        country: tempLocForm.country.trim(),
        latitude: tempLocForm.latitude ? parseFloat(tempLocForm.latitude) : null,
        longitude: tempLocForm.longitude ? parseFloat(tempLocForm.longitude) : null,
        timezone: tempLocForm.timezone.trim(),
        label: tempLocForm.label.trim(),
        start_date: tempLocForm.start_date,
        end_date: tempLocForm.end_date,
      })
      .select()
      .single();

    if (error) {
      captureMutationError(error, { operation: 'addTemporaryLocation', table: 'temporary_locations', employeeId: profileId });
      setTempLocError(error.message);
      return;
    }

    setTempLocs(prev => [...prev, {
      id: data.id,
      employeeId: data.employee_id,
      city: data.city,
      country: data.country,
      coordinates: { latitude: data.latitude ?? 0, longitude: data.longitude ?? 0 },
      timezone: data.timezone,
      label: data.label,
      startDate: data.start_date,
      endDate: data.end_date,
    }]);
    setTempLocForm({ city: '', country: '', timezone: '', label: '', start_date: '', end_date: '', latitude: '', longitude: '' });
    setShowTempLocForm(false);
  };

  const handleDeleteTempLoc = async (id: string) => {
    const { error } = await supabase.from('temporary_locations').delete().eq('id', id);
    if (error) {
      captureMutationError(error, { operation: 'deleteTemporaryLocation', table: 'temporary_locations', entityId: id });
    }
    setTempLocs(prev => prev.filter(l => l.id !== id));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Typing directly into lat/lng fields marks coordinates as manual
    if (name === 'latitude' || name === 'longitude') {
      setForm(f => ({ ...f, [name]: value, coordinates_manual: true }));
      setGeocodeSuggestion(null);
      return;
    }

    setForm(f => ({ ...f, [name]: value }));

    if (name === 'email') {
      const domain = value.split('@')[1]?.toLowerCase();
      const inferred = domain === 'stackblitz.com' ? 'StackBlitz' : '';
      if (inferred) setForm(f => f.employer ? f : { ...f, employer: inferred });
    }

    if (name === 'city' || name === 'country') {
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
      geocodeTimer.current = setTimeout(async () => {
        setForm(f => {
          const city = name === 'city' ? value : f.city;
          const country = name === 'country' ? value : f.country;
          if (!country) return f;
          setIsGeocoding(true);
          geocodeCityCountry(city, country).then(result => {
            if (result) {
              setForm(prev => {
                if (prev.coordinates_manual) {
                  // Don't auto-apply — show a confirmation prompt instead
                  setGeocodeSuggestion(result);
                  return prev;
                }
                return {
                  ...prev,
                  latitude: result.lat,
                  longitude: result.lon,
                  ...(result.timezone ? { timezone: result.timezone } : {}),
                };
              });
            }
            setIsGeocoding(false);
          });
          return f;
        });
      }, 600);
    }
  };

  const acceptGeocodeSuggestion = () => {
    if (!geocodeSuggestion) return;
    setForm(f => ({
      ...f,
      latitude: geocodeSuggestion.lat,
      longitude: geocodeSuggestion.lon,
      ...(geocodeSuggestion.timezone ? { timezone: geocodeSuggestion.timezone } : {}),
      coordinates_manual: false,
    }));
    setGeocodeSuggestion(null);
  };

  const dismissGeocodeSuggestion = () => {
    setGeocodeSuggestion(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setError(null);
    setCoordError(null);
    setIsSaving(true);

    // Validate coordinates if manually set — must fall within a country
    const lat = form.latitude ? parseFloat(form.latitude) : null;
    const lon = form.longitude ? parseFloat(form.longitude) : null;
    if (form.coordinates_manual && lat !== null && lon !== null) {
      const geo = await reverseGeocode(lat, lon);
      if (!geo) {
        setCoordError('These coordinates are not within any country. Please enter a valid location.');
        setIsSaving(false);
        return;
      }
    }

    const update: EmployeeUpdate = {
      user_id: user.id,
      name: form.name.trim(),
      role: form.role.trim(),
      department: form.department.trim(),
      employer: form.employer.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      bio: form.bio.trim(),
      start_date: form.start_date || null,
      skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
      languages: form.languages.split(',').map(s => s.trim()).filter(Boolean),
      city: form.city.trim(),
      country: form.country.trim(),
      latitude: lat,
      longitude: lon,
      timezone: form.timezone.trim(),
      avatar_url: form.avatar_url.trim() || null,
      avatar_hover_url: form.avatar_hover_url.trim() || null,
      social_media: {
        linkedin: form.social_linkedin.trim() || undefined,
        twitter: form.social_twitter.trim() || undefined,
        github: form.social_github.trim() || undefined,
        slack: form.social_slack.trim() || undefined,
      },
      profile_complete: true,
      new_hire_deck_url: form.new_hire_deck_url.trim() || null,
      coordinates_manual: form.coordinates_manual,
      meet_colleague_available: form.meet_colleague_available,
      meet_colleague_unavailable_until: form.meet_colleague_unavailable_until || null,
    };

    // Resolve the existing row ID — context profile, router-state claimed profile, or email lookup
    const existingId = employeeProfile?.id ?? claimedProfile?.id ?? null;

    try {
      if (existingId) {
        const { error } = await supabase
          .from('employees')
          .update(update)
          .eq('id', existingId);
        if (error) throw error;
      } else {
        // Check if there's a pre-existing row for this user's email (Google sign-in)
        const { data: existing } = await supabase
          .from('employees')
          .select('id')
          .eq('email', form.email.trim())
          .is('user_id', null)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from('employees')
            .update(update)
            .eq('id', existing.id);
          if (error) throw error;
        } else {
          // New employee: generate a slug from name
          const slug = form.name.trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
          const { error } = await supabase
            .from('employees')
            .insert({ ...update, slug });
          if (error) throw error;
        }
      }

      await refreshProfile();
      await reloadEmployees();
      const slug = employeeProfile?.slug ?? claimedProfile?.slug;
      navigate(slug ? `/employee/${slug}` : '/');
    } catch (err: unknown) {
      captureMutationError(err, { operation: 'saveProfile', table: 'employees', employeeId: existingId ?? undefined });
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      {!isOnboarding && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-bolt-textMuted hover:text-bolt-text transition-colors mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}

      {isOnboarding && (
        <div className="card p-5 mb-6 border-primary-600/30 bg-primary-600/5">
          <h2 className="text-sm font-semibold text-bolt-text mb-1">Welcome to the team directory!</h2>
          <p className="text-xs text-bolt-textMuted">
            Complete your profile so your teammates can find you on the map.
          </p>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-bolt-border flex items-center gap-2">
          <User className="h-4 w-4 text-bolt-textSubtle" />
          <h1 className="text-sm font-semibold text-bolt-text">
            {isOnboarding ? 'Set up your profile' : 'Edit profile'}
          </h1>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-6">
          {/* Basic info */}
          <Section icon={<User className="h-3.5 w-3.5" />} title="Basic info">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full name" required>
                <input name="name" value={form.name} onChange={handleChange} className="form-input" required />
              </Field>
              <Field label="Role / Title" required>
                <input name="role" value={form.role} onChange={handleChange} className="form-input" required />
              </Field>
              <Field label="Department" required>
                <input name="department" value={form.department} onChange={handleChange} className="form-input" required />
              </Field>
              <Field label="Employer" required>
                <input name="employer" value={form.employer} onChange={handleChange} className="form-input" required />
              </Field>
              <Field label="Email" required>
                <input name="email" type="email" value={form.email} onChange={handleChange} className="form-input" required />
              </Field>
              <Field label="Phone">
                <input name="phone" type="tel" value={form.phone} onChange={handleChange} className="form-input" />
              </Field>
              <Field label="Start date">
                <input name="start_date" type="date" value={form.start_date} onChange={handleChange} className="form-input" />
              </Field>
            </div>
            <Field label="Bio">
              <textarea name="bio" value={form.bio} onChange={handleChange} className="form-input resize-none h-20" />
            </Field>
            {user && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <AvatarUpload
                  userId={user.id}
                  currentUrl={form.avatar_url || null}
                  label="Profile photo"
                  storageKey="avatar"
                  onUploaded={url => setForm(f => ({ ...f, avatar_url: url }))}
                />
                <AvatarUpload
                  userId={user.id}
                  currentUrl={form.avatar_hover_url || null}
                  label="Hover photo (optional)"
                  storageKey="avatar-hover"
                  onUploaded={url => setForm(f => ({ ...f, avatar_hover_url: url }))}
                />
              </div>
            )}
          </Section>

          {/* Skills & Languages */}
          <Section icon={<Briefcase className="h-3.5 w-3.5" />} title="Skills & Languages">
            <Field label="Skills" hint="Comma-separated">
              <input name="skills" value={form.skills} onChange={handleChange} className="form-input" placeholder="Rails, Postgres, TypeScript" />
            </Field>
            <Field label="Languages" hint="Comma-separated">
              <input name="languages" value={form.languages} onChange={handleChange} className="form-input" placeholder="English, Spanish" />
            </Field>
          </Section>

          {/* Teams */}
          <Section icon={<Users className="h-3.5 w-3.5" />} title="Teams">
            <p className="text-xs text-bolt-textSubtle">
              Join interest or project-based teams to connect with teammates who share your passions.
            </p>

            {/* Current team memberships */}
            {teamsLoading ? (
              <div className="flex items-center gap-2 text-xs text-bolt-textSubtle">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading teams…
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 min-h-6">
                {allTeams
                  .filter(t => myTeamIds.has(t.id))
                  .map(team => (
                    <span key={team.id} className="tag-team cursor-default group relative pr-1">
                      <TeamIcon team={team} size={12} />
                      {team.name}
                      <button
                        type="button"
                        onClick={() => handleLeaveTeam(team.id)}
                        className="ml-1 text-emerald-400/60 hover:text-error-400 transition-colors"
                        title="Leave team"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))
                }
                {myTeamIds.size === 0 && (
                  <span className="text-xs text-bolt-textSubtle italic">No teams yet</span>
                )}
              </div>
            )}

            {/* Team picker dropdown */}
            {!showCreateTeam && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTeamDropdown(v => !v)}
                  className="flex items-center gap-1.5 text-xs text-bolt-textMuted hover:text-bolt-text border border-dashed border-bolt-border hover:border-bolt-borderLight rounded-lg px-3 py-2 transition-colors w-full justify-center"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Join a team
                </button>

                {showTeamDropdown && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-bolt-surface border border-bolt-border rounded-xl shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-bolt-border">
                      <input
                        type="text"
                        placeholder="Search teams…"
                        value={teamSearch}
                        onChange={e => setTeamSearch(e.target.value)}
                        className="form-input text-xs py-1.5 w-full"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto py-1">
                      {allTeams
                        .filter(t => !myTeamIds.has(t.id) && t.name.toLowerCase().includes(teamSearch.toLowerCase()))
                        .map(team => (
                          <button
                            key={team.id}
                            type="button"
                            onClick={() => handleJoinTeam(team)}
                            className="flex items-center gap-2.5 w-full px-3 py-2 text-xs text-bolt-textMuted hover:bg-bolt-surfaceHover hover:text-bolt-text transition-colors"
                          >
                            <TeamIcon team={team} size={14} />
                            {team.name}
                          </button>
                        ))
                      }
                      {allTeams.filter(t => !myTeamIds.has(t.id) && t.name.toLowerCase().includes(teamSearch.toLowerCase())).length === 0 && (
                        <p className="px-3 py-2 text-xs text-bolt-textSubtle italic">No matching teams</p>
                      )}
                    </div>
                    <div className="border-t border-bolt-border p-2">
                      <button
                        type="button"
                        onClick={() => { setShowTeamDropdown(false); setShowCreateTeam(true); setNewTeamName(teamSearch); }}
                        className="flex items-center gap-1.5 w-full px-3 py-2 text-xs text-primary-400 hover:bg-primary-600/10 rounded-lg transition-colors font-medium"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Create new team{teamSearch ? ` "${teamSearch}"` : ''}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Create new team inline form */}
            {showCreateTeam && (
              <div className="rounded-lg border border-bolt-border bg-bolt-bg p-4 space-y-3">
                <p className="text-xs font-semibold text-bolt-textMuted">Create a new team</p>
                <Field label="Team name" required>
                  <input
                    type="text"
                    value={newTeamName}
                    onChange={e => setNewTeamName(e.target.value)}
                    className="form-input"
                    placeholder="e.g. Team Rust"
                  />
                </Field>
                <div>
                  <label className="block text-xs text-bolt-textMuted mb-1.5">Icon</label>
                  <TeamIconPicker
                    value={newTeamIcon}
                    onChange={setNewTeamIcon}
                  />
                </div>
                {teamError && (
                  <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded px-3 py-2">{teamError}</p>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleCreateAndJoinTeam}
                    disabled={teamSaving}
                    className="btn btn-primary text-xs px-3 py-1.5 gap-1"
                  >
                    {teamSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    Create & join
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowCreateTeam(false); setTeamError(null); setNewTeamName(''); setNewTeamIcon({ iconType: 'emoji', iconValue: '' }); }}
                    className="btn btn-secondary text-xs px-3 py-1.5"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </Section>

          {/* Location */}
          <Section icon={<MapPin className="h-3.5 w-3.5" />} title="Location">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="City">
                <input name="city" value={form.city} onChange={handleChange} className="form-input" />
              </Field>
              <Field label="Country" required>
                <input name="country" value={form.country} onChange={handleChange} className="form-input" required />
              </Field>
              <div className="sm:col-span-2 grid grid-cols-3 gap-3">
                <Field label="Latitude" hint={isGeocoding ? 'detecting…' : form.coordinates_manual ? 'set manually' : 'auto-filled from city'}>
                  <div className="relative">
                    <input name="latitude" type="number" step="any" value={form.latitude} onChange={handleChange} className="form-input pr-8" />
                    {isGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
                  </div>
                </Field>
                <Field label="Longitude" hint={isGeocoding ? 'detecting…' : form.coordinates_manual ? 'set manually' : 'auto-filled from city'}>
                  <div className="relative">
                    <input name="longitude" type="number" step="any" value={form.longitude} onChange={handleChange} className="form-input pr-8" />
                    {isGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
                  </div>
                </Field>
                <Field label="Timezone" hint={isGeocoding ? 'detecting…' : 'auto-filled from city'} required>
                  <div className="relative">
                    <input name="timezone" value={form.timezone} onChange={handleChange} className="form-input pr-8" placeholder="America/New_York" required />
                    {isGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
                  </div>
                </Field>
              </div>
            </div>

            {/* Manual coordinates notice */}
            {form.coordinates_manual && !geocodeSuggestion && (
              <div className="flex items-start gap-2 rounded-lg border border-warning-500/30 bg-warning-500/8 px-3 py-2.5 text-xs text-warning-400">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Your map pin was placed manually. Make sure your city and country match your pin position.
                </span>
              </div>
            )}

            {/* Geocode suggestion prompt (shown when coordinates_manual=true and geocoding found a result) */}
            {geocodeSuggestion && (
              <div className="rounded-lg border border-primary-500/30 bg-primary-500/8 px-3 py-3 space-y-2">
                <p className="text-xs font-medium text-bolt-text">
                  Geocoding found coordinates for {form.city}, {form.country}:
                </p>
                <p className="text-xs text-bolt-textMuted font-mono">
                  {geocodeSuggestion.lat}, {geocodeSuggestion.lon}
                  {geocodeSuggestion.timezone && ` · ${geocodeSuggestion.timezone}`}
                </p>
                <p className="text-xs text-bolt-textSubtle">
                  Apply these instead of your manually placed pin?
                </p>
                <div className="flex items-center gap-2 pt-0.5">
                  <button
                    type="button"
                    onClick={acceptGeocodeSuggestion}
                    className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-primary-600 text-white hover:bg-primary-500 transition-colors"
                  >
                    <Check className="h-3 w-3" />
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={dismissGeocodeSuggestion}
                    className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-bolt-textMuted hover:text-bolt-text border border-bolt-border hover:border-bolt-borderLight transition-colors"
                  >
                    <X className="h-3 w-3" />
                    Keep my pin
                  </button>
                </div>
              </div>
            )}

            {coordError && (
              <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded px-3 py-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                {coordError}
              </p>
            )}

            {form.latitude && form.longitude && (
              <LocationPreview
                lat={parseFloat(form.latitude)}
                lon={parseFloat(form.longitude)}
                country={form.country}
              />
            )}
          </Section>

          {/* Meet a Colleague availability */}
          <Section icon={<Coffee className="h-3.5 w-3.5" />} title="Meet a Colleague">
            <p className="text-xs text-bolt-textSubtle mb-3">
              When enabled, colleagues may be matched with you for a virtual coffee chat.
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.meet_colleague_available}
                onChange={e => setForm(f => ({ ...f, meet_colleague_available: e.target.checked, meet_colleague_unavailable_until: e.target.checked ? '' : f.meet_colleague_unavailable_until }))}
                className="w-4 h-4 rounded border-bolt-border accent-teal-600"
              />
              <span className="text-sm text-bolt-text">Available for colleague matching</span>
            </label>
            {!form.meet_colleague_available && (
              <div className="mt-3 pl-7">
                <label className="block text-xs text-bolt-textMuted mb-1">
                  Pause until (leave empty to opt out permanently)
                </label>
                <input
                  type="date"
                  value={form.meet_colleague_unavailable_until}
                  onChange={e => setForm(f => ({ ...f, meet_colleague_unavailable_until: e.target.value }))}
                  className="form-input w-48"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}
          </Section>

          {/* Temporary Locations — only shown when editing an existing profile */}
          {(employeeProfile?.id || claimedProfile?.id) && (
            <Section icon={<Navigation className="h-3.5 w-3.5" />} title="Temporary Locations">
              <p className="text-xs text-bolt-textSubtle">
                Share your whereabouts with the team while traveling. Active locations appear as a pin on the map.
              </p>

              {tempLocsLoading ? (
                <div className="flex items-center gap-2 text-xs text-bolt-textSubtle py-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="space-y-2">
                  {tempLocs.map(loc => {
                    const today = new Date().toISOString().slice(0, 10);
                    const isActive = loc.startDate <= today && loc.endDate >= today;
                    return (
                      <div key={loc.id} className="flex items-start justify-between gap-3 rounded-lg border border-bolt-border bg-bolt-bg px-3 py-2.5">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-bolt-text">{loc.city}, {loc.country}</span>
                            {isActive && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-success-500/15 text-success-400 border border-success-500/20">
                                Active now
                              </span>
                            )}
                            {loc.label && (
                              <span className="text-xs text-bolt-textSubtle">{loc.label}</span>
                            )}
                          </div>
                          <div className="text-xs text-bolt-textSubtle mt-0.5">
                            {loc.startDate} → {loc.endDate}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteTempLoc(loc.id)}
                          className="flex-shrink-0 text-bolt-textSubtle hover:text-error-400 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {showTempLocForm ? (
                <div className="rounded-lg border border-bolt-border bg-bolt-bg p-4 space-y-3">
                  <p className="text-xs font-semibold text-bolt-textMuted">New temporary location</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="City" required>
                      <input name="city" value={tempLocForm.city} onChange={handleTempLocFieldChange} className="form-input" />
                    </Field>
                    <Field label="Country" required>
                      <input name="country" value={tempLocForm.country} onChange={handleTempLocFieldChange} className="form-input" />
                    </Field>
                    <Field label="Start date" required>
                      <input name="start_date" type="date" value={tempLocForm.start_date} onChange={handleTempLocFieldChange} className="form-input" />
                    </Field>
                    <Field label="End date" required>
                      <input name="end_date" type="date" value={tempLocForm.end_date} onChange={handleTempLocFieldChange} className="form-input" />
                    </Field>
                    <Field label="Latitude" hint={tempLocGeocoding ? 'detecting…' : 'auto-filled from city'}>
                      <div className="relative">
                        <input name="latitude" type="number" step="any" value={tempLocForm.latitude} onChange={handleTempLocFieldChange} className="form-input pr-8" placeholder="e.g. 48.8566" />
                        {tempLocGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
                      </div>
                    </Field>
                    <Field label="Longitude" hint={tempLocGeocoding ? 'detecting…' : 'auto-filled from city'}>
                      <div className="relative">
                        <input name="longitude" type="number" step="any" value={tempLocForm.longitude} onChange={handleTempLocFieldChange} className="form-input pr-8" placeholder="e.g. 2.3522" />
                        {tempLocGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
                      </div>
                    </Field>
                    <Field label="Label" hint="optional">
                      <input name="label" value={tempLocForm.label} onChange={handleTempLocFieldChange} className="form-input" placeholder="Conference, Client visit…" />
                    </Field>
                    <Field label="Timezone" hint={tempLocGeocoding ? 'detecting…' : 'auto-filled from city'}>
                      <div className="relative">
                        <input name="timezone" value={tempLocForm.timezone} onChange={handleTempLocFieldChange} className="form-input pr-8" placeholder="America/New_York" />
                        {tempLocGeocoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-bolt-textSubtle animate-spin" />}
                      </div>
                    </Field>
                  </div>

                  {tempLocError && (
                    <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded px-3 py-2">{tempLocError}</p>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <button type="button" onClick={handleAddTempLoc} className="btn btn-primary text-xs px-3 py-1.5 gap-1">
                      <Plus className="h-3.5 w-3.5" /> Add location
                    </button>
                    <button type="button" onClick={() => { setShowTempLocForm(false); setTempLocError(null); }} className="btn btn-secondary text-xs px-3 py-1.5">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowTempLocForm(true)}
                  className="flex items-center gap-1.5 text-xs text-bolt-textMuted hover:text-bolt-text border border-dashed border-bolt-border hover:border-bolt-borderLight rounded-lg px-3 py-2 transition-colors w-full justify-center"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add temporary location
                </button>
              )}
            </Section>
          )}

          {/* Pets — only shown when editing an existing profile */}
          {(employeeProfile?.id || claimedProfile?.id) && (
            <Section icon={<PawPrint className="h-3.5 w-3.5" />} title="Pets">
              <p className="text-xs text-bolt-textSubtle">
                Introduce your furry, feathered, or scaly companions. They'll appear on the map next to you.
              </p>

              {petsLoading ? (
                <div className="flex items-center gap-2 text-xs text-bolt-textSubtle py-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="space-y-2">
                  {pets.map(pet => (
                    <div key={pet.id} className="flex items-center gap-3 rounded-lg border border-bolt-border bg-bolt-bg px-3 py-2.5">
                      {(() => { const I = petSpeciesIcon(pet.species); return <I className="h-4 w-4 text-emerald-400 flex-shrink-0" />; })()}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-bolt-text">{pet.name}</span>
                          <span className="text-xs text-bolt-textSubtle capitalize">{pet.species}</span>
                          {pet.role && (
                            <span className="text-xs text-emerald-400">{pet.role}</span>
                          )}
                        </div>
                        {pet.bio && (
                          <p className="text-xs text-bolt-textSubtle mt-0.5 truncate">{pet.bio}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => openEditPetForm(pet)}
                          className="text-bolt-textSubtle hover:text-bolt-text transition-colors"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePet(pet.id)}
                          className="text-bolt-textSubtle hover:text-error-400 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showPetForm ? (
                <div className="rounded-lg border border-bolt-border bg-bolt-bg p-4 space-y-3">
                  <p className="text-xs font-semibold text-bolt-textMuted">
                    {editingPetId ? 'Edit pet' : 'Add a pet'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Name" required>
                      <input
                        type="text"
                        value={petForm.name}
                        onChange={e => setPetForm(f => ({ ...f, name: e.target.value }))}
                        className="form-input"
                        placeholder="Whiskers"
                      />
                    </Field>
                    <Field label="Species" required>
                      <select
                        value={petForm.species}
                        onChange={e => setPetForm(f => ({ ...f, species: e.target.value }))}
                        className="form-input"
                      >
                        {KNOWN_SPECIES.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Role" hint="optional">
                      <input
                        type="text"
                        value={petForm.role}
                        onChange={e => setPetForm(f => ({ ...f, role: e.target.value }))}
                        className="form-input"
                        placeholder="Chief Nap Officer"
                      />
                    </Field>
                    {user && (
                      <div className="sm:col-span-2">
                        <AvatarUpload
                          userId={user.id}
                          currentUrl={petForm.photo_url || null}
                          label="Photo"
                          storageKey={petStorageKey}
                          onUploaded={url => setPetForm(f => ({ ...f, photo_url: url }))}
                        />
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <Field label="Bio" hint="optional, markdown supported">
                        <textarea
                          value={petForm.bio}
                          onChange={e => setPetForm(f => ({ ...f, bio: e.target.value }))}
                          className="form-input resize-none h-20"
                          placeholder="Tell us about your pet…"
                        />
                      </Field>
                    </div>
                  </div>

                  {petError && (
                    <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded px-3 py-2">{petError}</p>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleSavePet}
                      disabled={petSaving}
                      className="btn btn-primary text-xs px-3 py-1.5 gap-1"
                    >
                      {petSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      {editingPetId ? 'Save changes' : 'Add pet'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowPetForm(false); setPetError(null); setEditingPetId(null); }}
                      className="btn btn-secondary text-xs px-3 py-1.5"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={openNewPetForm}
                  className="flex items-center gap-1.5 text-xs text-bolt-textMuted hover:text-bolt-text border border-dashed border-bolt-border hover:border-bolt-borderLight rounded-lg px-3 py-2 transition-colors w-full justify-center"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add a pet
                </button>
              )}
            </Section>
          )}

          {/* Social Media */}
          <Section icon={<Globe className="h-3.5 w-3.5" />} title="Social Media">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="GitHub username">
                <input name="social_github" value={form.social_github} onChange={handleChange} className="form-input" placeholder="username" />
              </Field>
              <Field label="LinkedIn handle">
                <input name="social_linkedin" value={form.social_linkedin} onChange={handleChange} className="form-input" placeholder="in/handle" />
              </Field>
              <Field label="Twitter / X handle">
                <input name="social_twitter" value={form.social_twitter} onChange={handleChange} className="form-input" placeholder="@handle" />
              </Field>
              <Field label="Slack display name">
                <input name="social_slack" value={form.social_slack} onChange={handleChange} className="form-input" />
              </Field>
            </div>
          </Section>

          {/* New Hire Deck */}
          <Section icon={<ExternalLink className="h-3.5 w-3.5" />} title="New Hire Deck">
            <div className="space-y-4">
              <Field label="Presentation URL" hint="Link to your onboarding deck presented to the team">
                <input
                  name="new_hire_deck_url"
                  type="url"
                  value={form.new_hire_deck_url}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://docs.google.com/presentation/..."
                />
              </Field>
            </div>
          </Section>

          {error && (
            <p className="text-xs text-error-400 bg-error-500/10 border border-error-500/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            {!isOnboarding && (
              <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary text-xs px-4 py-2">
                Cancel
              </button>
            )}
            <button type="submit" disabled={isSaving} className="btn btn-primary text-xs px-4 py-2 gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {isSaving ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Section = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <span className="text-bolt-textSubtle">{icon}</span>
      <span className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle">{title}</span>
    </div>
    <div className="pl-0 space-y-3">{children}</div>
  </div>
);

const Field = ({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs text-bolt-textMuted mb-1">
      {label}
      {required && <span className="text-error-400 ml-0.5">*</span>}
      {hint && <span className="ml-1 text-bolt-textSubtle">({hint})</span>}
    </label>
    {children}
  </div>
);

export default EditProfilePage;
